import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { Container, Row, Col, Table, Form, InputGroup, FormControl, Button, Spinner } from 'react-bootstrap';
import { ExploreContext } from '../../Context/ExploreContext';
import { App, ParentApp } from '../../interface';
import TagInput from '../../Components/TagInput';
import DemoModal from '../../Components/DemoModal/DemoModal';
import iconlink from '../../Assets/icons/icon-link.png'
import CustomAlert from '../../Components/CustomAlert/CustomAlert';
import TagSearchInput from '../../Components/TagSearchInput';

interface UserTabProps {
  searchTerm: string;
}

const ApplicationsTab: React.FC<UserTabProps> = ({ searchTerm }) => {
  const { getAllAdminApps, getAdminParentApps, saveAppData, getAppsPA, getSidebar, saveParentAppData, searchParentAppsAndApps } = useContext(ExploreContext);
  const [appData, setAppData] = useState<App[]>([]);
  const [parentAppData, setParentAppData] = useState<ParentApp[]>([]);
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [selectedParentApp, setSelectedParentApp] = useState<ParentApp | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);
  const [categoryApps, setCategoryApps] = useState<Record<number, App[]>>({});
  const [loadingCategory, setLoadingCategory] = useState<number | null>(null);
  const [isCategorySelected, setIsCategorySelected] = useState<boolean>(false);
  const [tagsArray, setTagsArray] = useState<string[]>([]);
  const [selectedAppsByPa, setSelectedAppsByPa] = useState<App[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [previewImagePreview, setPreviewImagePreview] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [alertVariant, setAlertVariant] = useState<'success' | 'danger' | 'warning' | 'info' | null>(null);
  const [selectedDenyAccessTags, setSelectedDenyAccessTags] = useState<string[]>([]);

  const [searchResults, setSearchResults] = useState<{
    parentApps: ParentApp[];
    apps: App[];
  } | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');

  const handleModal = () => {
    setShowModal(!showModal)
    setDataDemo(selectedParentApp)
  };
  const [dataDemo, setDataDemo] = useState<any>([]);

  const fetchAppsForCategory = async (categoryId: number) => {
    try {
      setLoadingCategory(categoryId);
      const apps = await getAppsPA(categoryId);
      setCategoryApps((prev) => ({ ...prev, [categoryId]: apps }));
    } catch (error) {
      console.error(`Error fetching apps for category ${categoryId}:`, error);
    } finally {
      setLoadingCategory(null);
    }
  };

  const toggleCategory = (categoryId: number) => {
    if (expandedCategories.includes(categoryId)) {
      setExpandedCategories((prev) => prev.filter((id) => id !== categoryId));
    } else {
      setExpandedCategories((prev) => [...prev, categoryId]);
      if (!categoryApps[categoryId]) {
        fetchAppsForCategory(categoryId);
      }
    }
  };

  const handleRowClick = (item: ParentApp | App, isCategory: boolean) => {
    if (isCategory) {
      setSelectedParentApp(item as ParentApp);
      setSelectedApp(null);
      setSelectedRowId(item.id); // Establecer el ID del ParentApp seleccionado
      setIsCategorySelected(true);
    } else {
      const appItem = item as App;
      setSelectedApp(appItem);
      setSelectedParentApp(null);
      setSelectedRowId(appItem.id); // Establecer el ID del App seleccionado
      setIsCategorySelected(false);
      const tags = appItem.tags_array ? appItem.tags_array : [];
      setTagsArray(tags);
    }
  };

  const getData = async () => {
    const aValue = localStorage.getItem('okta-token-storage')!;
    const oktadata = JSON.parse(aValue);
    const okta_id = oktadata.idToken.claims.sub;
    const response = await getAllAdminApps(okta_id);
    const parentApps = await getAdminParentApps(okta_id);

    setParentAppData(parentApps);
    setAppData(response.data);
  };

  useEffect(() => {
    getData();
  }, []);

  const handleTagChange = (
    field: 'what_offers' | 'benefits' | 'tags',
    tags: string[]
  ) => {
    if (isCategorySelected) {
      // Si está seleccionada una categoría (ParentApp)
      setSelectedParentApp((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          [field]: field === 'tags' ? tags : tags.join(','),
        } as ParentApp;
      });
    } else {
      // Si está seleccionada una aplicación (App)
      setSelectedApp((prevApp) => {
        if (!prevApp) return null;
        if (field === 'tags') {
          return {
            ...prevApp,
            tags_array: tags,
          };
        }
        return {
          ...prevApp,
          [field]: tags.join(','),
        };
      });
    }
  };


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = e.target;

    // Si es un switch (checkbox), convierte a 1 o 0
    const newValue = type === "checkbox" ? (checked ? 1 : 0) : value;

    if (isCategorySelected) {
      setSelectedParentApp((prev) => ({
        ...prev as ParentApp,
        [name]: newValue
      }));
    } else {
      setSelectedApp((prev) => ({
        ...prev as App,
        [name]: newValue
      }));
    }
  };


  const saveData = async () => {

    try {
      if (isCategorySelected && selectedParentApp) {
        // **✅ Convertir `deny_access` en JSON si existe**
        const parentAppToSend = {
          ...selectedParentApp,
          deny_access: selectedParentApp.deny_access
            ? JSON.stringify(selectedParentApp.deny_access)
            : "[]", // Enviar un string vacío si no hay deny_access
        }; // Debug para revisar la estructura

        // **✅ Convertir todo `parentAppToSend` en string JSON antes de enviarlo**
        const response = await saveParentAppData(parentAppToSend);

        if (response.success) {
          showAlert("Parent App saved successfully", "success");
          await getData();
          getSidebar();
          setExpandedCategories([]);
          setCategoryApps({});
          setSelectedParentApp(null);
        } else {
          showAlert("Failed to save Parent App", "danger");
        }
        setThumbnailPreview(null);
        setPreviewImagePreview(null);
      } else if (!isCategorySelected && selectedApp) {
        // Guardar un App
        const response = await saveAppData(selectedApp);
        if (response.success) {
          showAlert("App saved successfully", "success");
          await getData(); // Recarga los datos después de guardar
          setSelectedApp(null);
          setExpandedCategories([]);
          setCategoryApps({});
        } else {
          showAlert("Failed to save App", "danger");
        }
      } else {
        showAlert("No data selected to save", "danger");
      }
    } catch (error) {
      showAlert("An error occurred while saving data", "danger");
      console.error("Save error:", error);
    }
  };

  const handleImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "thumbnail" | "preview_img",
    id: number // Recibe el ID como parámetro
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileExtension = file.name.split('.').pop(); // Obtener la extensión del archivo
      const timestamp = Date.now(); // Obtener el timestamp actual
      const newFileName = `${field}_${id}_${timestamp}.${fileExtension}`; // Nuevo nombre del archivo

      // Crear un nuevo objeto File con el nombre modificado
      const renamedFile = new File([file], newFileName, { type: file.type });

      // Actualizar la previsualización local
      if (field === 'thumbnail') {
        setThumbnailPreview(URL.createObjectURL(renamedFile));
      } else {
        setPreviewImagePreview(URL.createObjectURL(renamedFile));
      }

      // Guardar el archivo en el estado como objeto File con el nuevo nombre
      setSelectedParentApp((prev) =>
        prev
          ? {
            ...prev,
            [field]: renamedFile, // Guardar el archivo con el nuevo nombre
          }
          : null
      );
    }
  };

  const cancelForm = () => {
    setSelectedApp(null);
    setExpandedCategories([]);
    setCategoryApps({})
    setSelectedParentApp(null)
    setThumbnailPreview(null)
    setPreviewImagePreview(null)
  }
  const searchValues = async (value: any) => {
    setSearchValue(value);

    if (value.trim()) {
      const results = await searchParentAppsAndApps(value);
      setSearchResults(results);
      setCategoryApps(results)
      setExpandedCategories(Object.keys(results).map(Number));
    } else {
      setSearchResults(null);
      setExpandedCategories([]);
    }
  }
  useEffect(() => {
    searchValues(searchTerm)


  }, [searchTerm]);

  const showAlert = (message: string, variant: 'success' | 'danger' | 'warning' | 'info') => {
    setAlertMessage(message);
    setAlertVariant(variant);

    setTimeout(() => {
      setAlertMessage(null);
      setAlertVariant(null);
    }, 5000); // Oculta la alerta después de 5 segundos
  };


  return (
    <>
      <Container fluid>
        <Row>
          <Col md={selectedParentApp || selectedApp ? 7 : 12}>
            <div
              className="shadow px-4"
              style={{
                maxHeight: '70vh',
                overflowY: 'auto',
              }}
            >
              <Table hover className='app-table'>
                <thead
                  style={{
                    position: 'sticky',
                    top: '0',
                  }}
                >
                  <tr>
                    <th className="bg-12 text-white border-0"></th>
                    <th className="bg-12 text-white border-0">ID</th>
                    <th className="bg-12 text-white border-0">App Name</th>
                    <th className="bg-12 text-white border-0">Client ID</th>
                    <th className="bg-12 text-white border-0">Institution Name</th>
                    <th className="bg-12 text-white border-0">Location</th>
                  </tr>
                </thead>
                <tbody>
                  {parentAppData.map((pa) => (
                    <React.Fragment key={pa.id}>
                      {/* Categoría */}
                      <tr
                        onClick={() => handleRowClick(pa, true)}
                        className={`cursor-pointer ${selectedRowId === pa.id && isCategorySelected ? 'selected-category-row' : ''
                          }`}
                      >
                        <td>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCategory(pa.id);
                            }}
                            className="btn-table-expand text-decoration-none bg-black"
                          >
                            {expandedCategories.includes(pa.id) ? '-' : '+'}
                          </button>
                        </td>
                        <td>{pa.id}</td>
                        <td colSpan={5} className="bg-light">
                          <span>{pa.parent_app_name}</span>
                        </td>
                      </tr>

                      {/* Aplicaciones dentro de la categoría */}
                      {expandedCategories.includes(pa.id) && (
                        <>
                          {loadingCategory === pa.id ? (
                            <tr className="row-app">
                              <td colSpan={6} className="text-center">
                                <Spinner animation="border" size="sm" /> Loading...
                              </td>
                            </tr>
                          ) : categoryApps[pa.id]?.length > 0 ? (
                            categoryApps[pa.id].map((app) => (
                              <tr
                                key={app.id}
                                onClick={() => handleRowClick(app, false)}
                                className={`cursor-pointer row-app ${selectedRowId === app.id && !isCategorySelected ? 'selected-app-row' : ''
                                  }`}
                              >
                                <td></td>
                                <td></td>
                                <td>{app.app_name}</td>
                                <td>{app.accountOktaGroup?.account?.huspot_id}</td>
                                <td>{app.accountOktaGroup?.account?.institution_name}</td>
                                <td>{app.location}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className="text-center text-muted">
                                No related applications
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>


              </Table>
            </div>
          </Col>
          {(selectedParentApp || selectedApp) && (
            <Col md={5}>
              {isCategorySelected && selectedParentApp && (
                <Form>
                  <Form.Group controlId="categoryName">
                    <Form.Label>Aplication Name</Form.Label>
                    <Form.Control
                      type="text"
                      name='parent_app_name'
                      value={selectedParentApp.parent_app_name || ''}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group controlId="description" className="mb-3">
                    <Form.Label className='mb-1'>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder=""
                      name='description'
                      value={selectedParentApp.description || ''}
                      onChange={handleChange}
                      className='rounded-0'
                    />
                  </Form.Group>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group controlId="whatItOffers">
                        <TagInput
                          label="What it offers"
                          tags={selectedParentApp.what_offers ? selectedParentApp.what_offers.split(',') : []}
                          onTagsChange={(tags) => handleTagChange('what_offers', tags)}
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group controlId="benefits">
                        <TagInput
                          label="Benefits"
                          tags={selectedParentApp.benefits ? selectedParentApp.benefits.split(',') : []}
                          onTagsChange={(tags) => handleTagChange('benefits', tags)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <Form.Check
                        type="switch"
                        id="custom-switch"
                        name="access_to_all"
                        checked={!!selectedParentApp?.access_to_all} // Convierte `1` en `true` y `0` en `false`
                        onChange={handleChange}
                        label="Access to All"
                      />
                    </Col>

                    <Col>
                      {selectedParentApp?.access_to_all ? (
                        <Form.Group controlId="denyAccess">
                          <Form.Label>Deny Access</Form.Label>

                          <TagSearchInput
                            selectedTags={selectedParentApp?.deny_access || []}
                            onTagsChange={(tags) => {
                              setSelectedParentApp((prev) => {
                                if (!prev) return null;
                                return {
                                  ...prev,
                                  deny_access: tags,
                                };
                              });
                            }}
                          />
                        </Form.Group>
                      ) : null}
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    {/* <Col md={6}>
                      <Form.Group controlId="thumbnail">
                        <Form.Label>Thumbnail</Form.Label>
                        <input
                          type="file"
                          accept="image/*"
                          className='form-control'
                          onChange={(e) => handleImageChange(e, "thumbnail",selectedParentApp.id)}
                        />
                        {thumbnailPreview && (
                          <img
                            src={thumbnailPreview}
                            alt="Thumbnail preview"
                            className="mt-3"
                            style={{ width: "100%", maxHeight: "200px", objectFit: "contain" }}
                          />
                        )}
                      </Form.Group>
                    </Col> */}
                    <Col md={6}>
                      <Form.Group controlId="preview_img">
                        <Form.Label>Preview Image</Form.Label>
                        <input
                          type="file"
                          accept="image/*"
                          className='form-control'
                          onChange={(e) => handleImageChange(e, "preview_img", selectedParentApp.id)}
                        />
                        {previewImagePreview && (
                          <img
                            src={previewImagePreview}
                            alt="Preview Image"
                            className="mt-3"
                            style={{ width: "100%", maxHeight: "200px", objectFit: "contain" }}
                          />
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                  <div className="d-grid gap-2 col-10 mx-auto" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    <Button variant="outline-dark" onClick={cancelForm}>Cancel</Button>
                    <Button variant="outline-dark" onClick={handleModal} >Preview</Button>
                    <Button variant="dark" onClick={saveData}>Apply Changes</Button>
                  </div>
                </Form>
              )}

              {!isCategorySelected && selectedApp && (
                <Form>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group controlId="clientID">
                        <Form.Label className='mb-1'>Client ID</Form.Label>
                        <Form.Control type="text" value={selectedApp.id} disabled className='rounded-0' />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group controlId="institution">
                        <Form.Label className='mb-1'>Institution</Form.Label>
                        <Form.Control
                          type="text"
                          value={selectedApp.accountOktaGroup?.account?.institution_name}
                          disabled
                          className='rounded-0'
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col>
                      <Form.Group controlId="applicationName">
                        <Form.Label className='mb-1'>Application Name</Form.Label>
                        <Form.Control
                          type="text"
                          name='app_name'
                          value={selectedApp.app_name || ''}
                          onChange={handleChange}
                          className='rounded-0'
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group controlId="parentApp">
                        <Form.Label className='mb-1'>Parent App</Form.Label>
                        <Form.Select
                          value={selectedApp?.parentApp?.id || ''}
                          onChange={(e) => {
                            const selectedParentAppId = parseInt(e.target.value, 10);
                            setSelectedApp((prevApp) => {
                              if (!prevApp) return null;

                              return {
                                ...prevApp,
                                parentApp: {
                                  ...prevApp.parentApp,
                                  id: selectedParentAppId,
                                  selected_link: prevApp.parentApp?.selected_link || '', // Asegura que siempre sea un string
                                  oktaAppLink: prevApp.parentApp?.oktaAppLink ?? null, // Asegura que no falte esta propiedad si es obligatoria
                                  app_id: prevApp.parentApp?.app_id ?? undefined,
                                  account_okta_group_id: prevApp.parentApp?.account_okta_group_id ?? undefined,
                                  app_name: prevApp.parentApp?.app_name ?? undefined,
                                  link_on_premise: prevApp.parentApp?.link_on_premise ?? undefined,
                                  link_cloud: prevApp.parentApp?.link_cloud ?? undefined,
                                  is_recommended: prevApp.parentApp?.is_recommended ?? undefined,
                                  demo_link: prevApp.parentApp?.demo_link ?? undefined,
                                },
                              };
                            });
                          }}
                          className='rounded-0'
                        >
                          <option value="">Seleccione una opción</option>
                          {parentAppData.map((pa) => (
                            <option key={pa.id} value={pa.id}>
                              {pa.parent_app_name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col>
                      <Form.Group controlId="onPremiseLink">
                        <Form.Label className='mb-1'>Application Link on Premise</Form.Label>
                        <InputGroup className='custom-input-icon'>
                          <FormControl
                            placeholder=""
                            value={selectedApp.link_on_premise || ''}
                            name='link_on_premise'
                            onChange={handleChange}
                            className='rounded-0'
                          />
                          <a href={selectedApp.link_on_premise} target='_blank' className='form-control-icon'>

                            <img src={iconlink} alt="" />
                          </a>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group controlId="onCloudLink">
                        <Form.Label className='mb-1'>Application Link on Cloud</Form.Label>
                        <InputGroup className='custom-input-icon'>
                          <FormControl
                            placeholder=""
                            value={selectedApp.link_cloud || ''}
                            name='link_cloud'
                            onChange={handleChange}
                            className='rounded-0'
                          />
                          <a href={selectedApp.link_cloud} target='_blank' className='form-control-icon'>

                            <img src={iconlink} alt="" />
                          </a>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group controlId="description" className="mb-3">
                    <Form.Label className='mb-1'>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder=""
                      name='app_description'
                      value={selectedApp.app_description || ''}
                      onChange={handleChange}
                      className='rounded-0'
                    />
                  </Form.Group>
                  <Col>
                    <Form.Group controlId="tags">
                      <TagInput
                        label="Tags"
                        tags={tagsArray ? tagsArray : []}
                        onTagsChange={(tags) => handleTagChange('tags', tags)}
                      />
                    </Form.Group>
                  </Col>

                  <div className="d-grid gap-2 col-10 mx-auto" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    <Button variant="outline-dark" onClick={cancelForm} >Cancel</Button>
                    {/* <Button variant="outline-dark" >Preview</Button> */}
                    <Button variant="dark" onClick={saveData}>Apply Changes</Button>
                  </div>
                </Form>
              )}
            </Col>
          )}

        </Row>
      </Container>
      <DemoModal showModal={showModal} handleModal={handleModal} dataDemo={dataDemo} img1={previewImagePreview} img2={thumbnailPreview} />
      {alertMessage && (
        <CustomAlert
          message={alertMessage}
          variant={alertVariant!}
          onClose={() => setAlertMessage(null)}
        />
      )}
    </>
  );
};

export default ApplicationsTab;
